"use client"
import React from "react"
import { extractColorSwatchesFromProducts } from "@/app/(site)/_utils/transformData/extractColorSwatchesFromProducts"
import {
    selectColorSwatch,
    setColorSwatches,
    updateCanAddToCart,
    setAvailableSizes,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import { SizeInfo } from "@/lib/content-service/types/ContentServiceProduct"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import ColorSwatch from "@/app/(site)/_components/Modules/Cart/QuickShop/ColorSwatchesModal/ColorSwatch"

interface ColorSwatchesModalProps {
    allRelatedVariants: (CheckoutAPIProduct | RelatedProduct)[]
    sizes: SizeInfo[]
    onSwatchSelect: (swatchUri: string) => void
}

const ColorSwatchesModal: React.FC<ColorSwatchesModalProps> = ({
    allRelatedVariants,
    sizes,
    onSwatchSelect
}) => {
    const dispatch = useAppDispatch()
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)
    const selectedColor = useAppSelector(state => state.product.selectedColor)
    const colorSwatches = extractColorSwatchesFromProducts(allRelatedVariants)

    React.useEffect(() => {
        dispatch(setColorSwatches(colorSwatches))
        dispatch(selectColorSwatch(colorSwatches?.[0]?.color_name))
    }, [colorSwatches])

    const handleChangeVariant = (selectedColorSwatch: string) => {
        const selectedSize = sizes.find(s => s.isAvailable)?.sizeId
        const updatedSizes = availableSizesContext.map(s => ({
            ...s,
            isSelected: s.sizeId === selectedSize
        }))
        dispatch(setAvailableSizes(updatedSizes))
        dispatch(selectColorSwatch(selectedColorSwatch))
        dispatch(updateCanAddToCart())
    }

    const sortedSwatches = React.useMemo(() => {
        return [...colorSwatches].sort((a, b) => {
            const orderA = a.sortOrder || Infinity
            const orderB = b.sortOrder || Infinity
            return orderA - orderB || (a.uri || "").localeCompare(b.uri || "")
        })
    }, [colorSwatches])

    return (
        <div className="flex flex-wrap items-center justify-start gap-3 md:gap-8">
            {sortedSwatches.map((swatch, index) => (
                <ColorSwatch
                    key={swatch.uri || index}
                    swatch={swatch}
                    selectedColor={selectedColor}
                    onSwatchSelect={onSwatchSelect}
                    handleChangeVariant={() => handleChangeVariant(swatch.color_name)}
                />
            ))}
        </div>
    )
}

export default ColorSwatchesModal
